<div class="flex flex-col gap-6">
  @if (showHero()) {
    <!-- Top Hero Header with Mesh Gradient & Context -->
    <div
      class="relative overflow-hidden rounded-2xl border border-indigo-a4/60 bg-gradient-to-br from-indigo-a2/80 via-surface-default to-purple-a2/40 p-6 shadow-xs"
    >
      <!-- Ambient light glow -->
      <div
        class="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-indigo-a3 blur-3xl"
      ></div>

      <div class="relative flex flex-col gap-3.5">
        <!-- Top Row: Title on Left, Location Badge & Action on Right -->
        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div class="flex items-center gap-3 min-w-0">
            <div
              class="h-10 w-10 sm:h-11 sm:w-11 flex items-center justify-center rounded-xl bg-indigo-9 text-indigo-contrast shadow-sm shadow-indigo-9/20 shrink-0"
            >
              <mat-icon class="text-xl sm:text-2xl leading-none text-indigo-contrast"
                >travel_explore</mat-icon
              >
            </div>
            <div class="min-w-0">
              <h3 class="text-lg sm:text-xl font-bold tracking-tight text-gray-12 m-0 truncate">
                Données géospatiales
              </h3>
            </div>
          </div>

          <!-- Actions & Location -->
          <div class="flex flex-wrap items-center gap-2.5 sm:shrink-0">
            @if ((isOwner() || isAdminApp()) && addressLabel()) {
              <div
                class="flex items-center gap-1.5 text-xs text-gray-12 bg-surface-default/90 border border-gray-5 px-3 py-1.5 rounded-full shadow-2xs"
              >
                <mat-icon fontIcon="place" class="text-sm leading-none text-indigo-11"></mat-icon>
                <span class="font-medium truncate max-w-xs sm:max-w-sm">{{ addressLabel() }}</span>
              </div>
            } @else if (cityName()) {
              <div
                class="flex items-center gap-1.5 text-xs text-gray-12 bg-surface-default/90 border border-gray-5 px-3 py-1.5 rounded-full shadow-2xs"
              >
                <mat-icon fontIcon="place" class="text-sm leading-none text-indigo-11"></mat-icon>
                <span class="font-medium truncate max-w-xs sm:max-w-sm">{{ cityName() }}</span>
              </div>
            }

            @if (showRefreshButton()) {
              <button
                mat-stroked-button
                class="text-xs bg-surface-default hover:bg-gray-2 transition-colors !h-8 !px-3"
                [disabled]="isLoading()"
                (click)="fetchGeospatialData()"
                type="button"
              >
                <span class="inline-flex items-center gap-1.5 font-medium text-gray-12">
                  @if (isLoading()) {
                    <mat-spinner diameter="14"></mat-spinner>
                    <span>Synchronisation...</span>
                  } @else {
                    <mat-icon fontIcon="sync" class="text-sm leading-none text-indigo-11"></mat-icon>
                    <span>Actualiser les données</span>
                  }
                </span>
              </button>
            }

            @if (collapsible()) {
              <button
                mat-stroked-button
                type="button"
                class="text-xs bg-surface-default hover:bg-gray-2 transition-colors !h-8 !px-3"
                (click)="isMainExpanded.set(!isMainExpanded())"
              >
                <span class="inline-flex items-center gap-1.5 font-medium text-gray-12">
                  <mat-icon
                    [fontIcon]="isMainExpanded() ? 'expand_less' : 'expand_more'"
                    class="text-sm leading-none text-indigo-11"
                  ></mat-icon>
                  <span>{{ isMainExpanded() ? 'Replier le volet' : 'Déplier les analyses' }}</span>
                </span>
              </button>
            }
          </div>
        </div>

        <!-- Description: Full width, comfortable reading line -->
        <p class="text-xs sm:text-sm text-gray-11 m-0 leading-relaxed max-w-3xl">
          Cartographie approfondie et consolidation automatique des registres publics : cadastre, géorisques,
          revenus INSEE, acoustique et urbanisme.
        </p>
      </div>

      <!-- Quick Navigation Ticker / Executive Glance (HUD) -->
      <div class="relative mt-6 grid grid-cols-2 gap-2 sm:grid-cols-4 border-t border-indigo-a4/40 pt-4">
        <!-- HUD 2: Géorisques -->
        <button
          type="button"
          (click)="scrollToSection('section-geohazards')"
          class="flex flex-col rounded-xl border border-gray-5/80 bg-surface-default/80 p-2.5 text-left transition-all duration-150 hover:bg-surface-default hover:border-amber-a4 hover:shadow-2xs group cursor-pointer"
        >
          <span class="text-xs font-semibold text-gray-10 uppercase tracking-wider flex items-center gap-1.5">
            <mat-icon fontIcon="shield" class="text-sm text-amber-11 leading-none shrink-0"></mat-icon>
            Géorisques
          </span>
          <span
            class="text-sm font-bold truncate mt-1"
            [class]="risksSummary()?.isSafe ? 'text-jade-11' : 'text-amber-11'"
          >
            @if (risksSummary(); as r) {
              {{ r.isSafe ? 'Secteur préservé' : r.count > 1 ? r.count + ' risques' : r.count + ' risque' }}
            } @else {
              Analysé
            }
          </span>
        </button>

        <!-- HUD 3: INSEE -->
        <button
          type="button"
          (click)="scrollToSection('section-insee')"
          class="flex flex-col rounded-xl border border-gray-5/80 bg-surface-default/80 p-2.5 text-left transition-all duration-150 hover:bg-surface-default hover:border-purple-a4 hover:shadow-2xs group cursor-pointer"
        >
          <span class="text-xs font-semibold text-gray-10 uppercase tracking-wider flex items-center gap-1.5">
            <mat-icon fontIcon="analytics" class="text-sm text-purple-11 leading-none shrink-0"></mat-icon>
            Revenus INSEE
          </span>
          <span class="text-sm font-bold text-gray-12 truncate mt-1">
            {{
              insee()?.medianIncome
                ? (insee()?.medianIncome | currency: 'EUR' : 'symbol' : '1.0-0' : 'fr-FR')
                : 'Médian'
            }}
          </span>
        </button>

        <!-- HUD 4: Acoustique -->
        <button
          type="button"
          (click)="scrollToSection('section-noise')"
          class="flex flex-col rounded-xl border border-gray-5/80 bg-surface-default/80 p-2.5 text-left transition-all duration-150 hover:bg-surface-default hover:border-blue-a4 hover:shadow-2xs group cursor-pointer"
        >
          <span class="text-xs font-semibold text-gray-10 uppercase tracking-wider flex items-center gap-1.5">
            <mat-icon fontIcon="volume_up" class="text-sm text-blue-11 leading-none shrink-0"></mat-icon>
            Acoustique
          </span>
          <span class="text-sm font-bold text-gray-12 truncate mt-1">
            {{ noise()?.levelDb ? noise()?.levelDb + ' dB(A)' : 'Calme' }}
          </span>
        </button>

        <!-- HUD 5: Urbanisme -->
        <button
          type="button"
          (click)="scrollToSection('section-sitadel')"
          class="col-span-2 sm:col-span-1 flex flex-col rounded-xl border border-gray-5/80 bg-surface-default/80 p-2.5 text-left transition-all duration-150 hover:bg-surface-default hover:border-orange-a4 hover:shadow-2xs group cursor-pointer"
        >
          <span class="text-xs font-semibold text-gray-10 uppercase tracking-wider flex items-center gap-1.5">
            <mat-icon fontIcon="apartment" class="text-sm text-orange-11 leading-none shrink-0"></mat-icon>
            Urbanisme
          </span>
          <span class="text-sm font-bold text-gray-12 truncate mt-1">
            {{
              sitadel()?.unitsUnderConstruction !== undefined
                ? sitadel()?.unitsUnderConstruction + ' logts neufs'
                : 'Sitadel 500m'
            }}
          </span>
        </button>
      </div>

      <!-- Collapsible Footer Trigger -->
      @if (collapsible()) {
        <div class="mt-4 pt-3 border-t border-indigo-a4/30 flex items-center justify-between">
          <span class="text-xs text-gray-11 font-medium">
            @if (isMainExpanded()) {
              4 analyses détaillées disponibles (Géorisques, Acoustique, Urbanisme, INSEE)
            } @else {
              Cliquez pour afficher les 4 volets thématiques
            }
          </span>
          <button
            mat-button
            type="button"
            class="text-xs text-indigo-11 hover:bg-indigo-a2 font-semibold !h-8 !px-3 cursor-pointer"
            (click)="isMainExpanded.set(!isMainExpanded())"
          >
            <span class="inline-flex items-center gap-1.5">
              <span>{{ isMainExpanded() ? 'Replier les volets' : 'Déplier les volets' }}</span>
              <mat-icon
                [fontIcon]="isMainExpanded() ? 'expand_less' : 'expand_more'"
                class="text-sm leading-none"
              ></mat-icon>
            </span>
          </button>
        </div>
      }
    </div>
  }

  @if (!collapsible() || isMainExpanded()) {
    <!-- Loading banner if initial fetch -->
    @if (isLoading() && !geospatial()) {
      <div
        class="flex flex-col items-center justify-center gap-4 py-16 border-2 border-dashed border-gray-6 rounded-2xl bg-gray-2"
      >
        <mat-spinner diameter="40"></mat-spinner>
        <div class="text-center">
          <p class="text-sm font-semibold text-gray-12 m-0">
            Récupération des données géospatiales en cours...
          </p>
          <p class="text-xs text-gray-11 m-0 mt-1">
            Interrogation des registres officiels de l'État (DGFiP, Géorisques, INSEE, Sitadel, Cerema).
          </p>
        </div>
      </div>
    } @else {
      @if (error()) {
        <!-- Error Alert -->
        <div
          class="flex items-center justify-between gap-4 rounded-2xl border border-ruby-a4 bg-ruby-a2 p-4 text-ruby-11"
        >
          <div class="flex items-center gap-3">
            <mat-icon fontIcon="error_outline" class="text-2xl shrink-0"></mat-icon>
            <p class="text-sm font-medium m-0">{{ error() }}</p>
          </div>
          <button
            mat-button
            class="text-xs text-ruby-11 hover:bg-ruby-a3"
            (click)="fetchGeospatialData()"
            type="button"
          >
            Réessayer
          </button>
        </div>
      }

      <!-- Accordion toggle toolbar -->
      <div class="flex items-center justify-between px-1 pt-1">
        <span class="text-xs font-semibold text-gray-11 uppercase tracking-wider">
          Volets thématiques géospatiaux
        </span>
        <button
          mat-button
          type="button"
          class="text-xs text-indigo-11 hover:bg-indigo-a2 transition-colors !h-8 !px-3"
          (click)="toggleAllSections()"
        >
          <span class="inline-flex items-center gap-1.5 font-medium">
            <mat-icon
              [fontIcon]="allExpanded() ? 'unfold_less' : 'unfold_more'"
              class="text-sm leading-none"
            ></mat-icon>
            <span>{{ allExpanded() ? 'Tout replier' : 'Tout déplier' }}</span>
          </span>
        </button>
      </div>

      <!-- 2. Geohazard (Géorisques) -->
      <div id="section-geohazards" class="scroll-mt-6">
        <etxe-property-geohazards-card
          [geohazard]="geohazard()"
          [addressLabel]="addressLabel()"
          [(expanded)]="geohazardsExpanded"
        ></etxe-property-geohazards-card>
      </div>

      <!-- 3. Noise (Environnement sonore & Acoustique) -->
      <div id="section-noise" class="scroll-mt-6">
        <etxe-property-noise-card [noise]="noise()" [(expanded)]="noiseExpanded"></etxe-property-noise-card>
      </div>

      <!-- 4. Sitadel (Permis de construire & Urbanisme) -->
      <div id="section-sitadel" class="scroll-mt-6">
        <etxe-property-sitadel-card
          [sitadel]="sitadel()"
          [(expanded)]="sitadelExpanded"
        ></etxe-property-sitadel-card>
      </div>

      <!-- 5. INSEE (Revenus & Niveau de vie) -->
      <div id="section-insee" class="scroll-mt-6">
        <etxe-property-insee-card
          [insee]="insee()"
          [cityName]="cityName()"
          [(expanded)]="inseeExpanded"
        ></etxe-property-insee-card>
      </div>
    }
  }
</div>
